import { useState, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import axios from "axios";
import MDBox from "admin/components/MDBox";
import MDTypography from "admin/components/MDTypography";
import MDInput from "admin/components/MDInput";
import DashboardLayout from "admin/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "admin/examples/Navbars/DashboardNavbar";

const API_URL = process.env.REACT_APP_APIHOST || "http://localhost:3033";

function AdminArsenal() {
    const [weapons, setWeapons] = useState([]);
    const [arsenal, setArsenal] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterRole, setFilterRole] = useState('all');
    const [filterOperator, setFilterOperator] = useState('all');
    const [showAdd, setShowAdd] = useState(false);
    const [newWeapon, setNewWeapon] = useState({name:'',designation:'',manufacturer:'',operator:'',type:'missile',range_km:0,warhead:'',role:'offensive'});
    const fileRefs = useRef({});

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [weaponsRes, arsenalRes] = await Promise.all([
                axios.get(API_URL + "/arsenal/weapons"),
                axios.get(API_URL + "/arsenal/catalog"),
            ]);
            if (weaponsRes.data.status) setWeapons(weaponsRes.data.data);
            if (arsenalRes.data.status) setArsenal(arsenalRes.data.data || []);
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleAddWeapon = async () => {
        if (!newWeapon.name) return;
        try {
            await axios.post(API_URL + "/arsenal/weapons", { weaponName: newWeapon.name, imageUrl: '', filename: '' });
            // Also add to arsenal cache catalog
            const m = require; // just save basic info
            fetchData();
            setShowAdd(false);
            setNewWeapon({name:'',designation:'',manufacturer:'',operator:'',type:'missile',range_km:0,warhead:'',role:'offensive'});
        } catch(e) { console.error(e); }
    };

    const handleToggleVisible = async (name, currentVisible) => {
        try {
            await axios.put(API_URL + "/arsenal/catalog/toggle", { name, visible: !currentVisible });
            fetchData();
        } catch(e) { console.error(e); }
    };

    const handleUploadImage = async (weaponName, file) => {
        try {
            const fd = new FormData(); fd.append("image", file);
            const token = localStorage.getItem("token");
            const uploadRes = await axios.post(API_URL + "/upload", fd, { headers: { "Content-Type": "multipart/form-data", Authorization: "Bearer " + token } });
            if (uploadRes.data.status) {
                await axios.post(API_URL + "/arsenal/weapons", { weaponName, imageUrl: uploadRes.data.data.url, filename: uploadRes.data.data.filename });
                fetchData();
            }
        } catch(e) { console.error(e); }
    };

    const handleDelete = async (id) => {
        await axios.delete(API_URL + "/arsenal/weapons/" + id);
        fetchData();
    };

    // Merge: show all arsenal items + existing images
    const imgMap = {};
    weapons.forEach(w => { imgMap[w.weaponName.toLowerCase()] = w; });

    const allItems = arsenal.map(a => ({
        ...a,
        dbEntry: imgMap[a.name.toLowerCase()] || null,
        hasImage: !!imgMap[a.name.toLowerCase()]?.imageUrl,
        visible: a.visible !== false,
    }));

    const operators = [...new Set(allItems.map(w => w.operator).filter(Boolean))].sort();

    const filteredItems = allItems.filter(w => {
        if (filterRole !== 'all' && w.role !== filterRole) return false;
        if (filterOperator !== 'all' && w.operator !== filterOperator) return false;
        return true;
    });

    const roleColor = (r) => r === "offensive" ? "#e55" : "#4CAF50";

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={2} pb={2}>
                <Card>
                    <MDBox px={2} pt={1} pb={0.5} display="flex" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="body2" sx={{ color: "#C29717", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1.5px" }}>
                            Arsenal ({filteredItems.length}/{allItems.length})
                        </MDTypography>
                        <MDBox display="flex" gap={1} alignItems="center">
                            <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={{background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'2px',padding:'3px 8px',fontSize:'11px',color:'#ccc',outline:'none'}}>
                                <option value="all">All roles</option>
                                <option value="offensive">Offensive</option>
                                <option value="defensive">Defensive</option>
                            </select>
                            <select value={filterOperator} onChange={e => setFilterOperator(e.target.value)} style={{background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'2px',padding:'3px 8px',fontSize:'11px',color:'#ccc',outline:'none'}}>
                                <option value="all">All operators</option>
                                {operators.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                            <MDTypography component="span" sx={{ color: "#aaa", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", "&:hover": { color: "#fff" } }} onClick={() => setShowAdd(!showAdd)}>
                                <Icon sx={{ fontSize: "14px !important" }}>add_circle_outline</Icon> Add
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                    {showAdd && (
                        <MDBox px={2} py={1} sx={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                            <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                <input placeholder="Weapon name *" value={newWeapon.name} onChange={e => setNewWeapon({...newWeapon, name: e.target.value})} style={{background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'2px',padding:'5px 8px',fontSize:'11px',color:'#fff',outline:'none',width:'180px'}} />
                                <input placeholder="Designation" value={newWeapon.designation} onChange={e => setNewWeapon({...newWeapon, designation: e.target.value})} style={{background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'2px',padding:'5px 8px',fontSize:'11px',color:'#fff',outline:'none',width:'120px'}} />
                                <input placeholder="Manufacturer" value={newWeapon.manufacturer} onChange={e => setNewWeapon({...newWeapon, manufacturer: e.target.value})} style={{background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'2px',padding:'5px 8px',fontSize:'11px',color:'#fff',outline:'none',width:'120px'}} />
                                <input placeholder="Operator" value={newWeapon.operator} onChange={e => setNewWeapon({...newWeapon, operator: e.target.value})} style={{background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'2px',padding:'5px 8px',fontSize:'11px',color:'#fff',outline:'none',width:'120px'}} />
                                <select value={newWeapon.role} onChange={e => setNewWeapon({...newWeapon, role: e.target.value})} style={{background:'#2a2a2a',border:'1px solid rgba(255,255,255,0.12)',borderRadius:'2px',padding:'5px 8px',fontSize:'11px',color:'#ccc',outline:'none'}}>
                                    <option value="offensive">Offensive</option>
                                    <option value="defensive">Defensive</option>
                                </select>
                                <span onClick={handleAddWeapon} style={{padding:'5px 12px',background:'rgba(194,151,23,0.15)',border:'1px solid rgba(194,151,23,0.3)',borderRadius:'2px',color:'#C29717',fontSize:'11px',cursor:'pointer'}}>Save</span>
                            </MDBox>
                        </MDBox>
                    )}
                    <MDBox px={2} py={1}>
                        {loading ? (
                            <MDBox display="flex" justifyContent="center" p={3}><div className="forte-spin" style={{margin:"0 auto"}}></div></MDBox>
                        ) : filteredItems.length === 0 ? (
                            <MDTypography sx={{ color: "#888", fontSize: "13px", py: 2, textAlign: "center" }}>No weapons detected yet. Visit Daily Reports to generate analysis.</MDTypography>
                        ) : (
                            filteredItems.map((w, i) => (
                                <MDBox key={i} sx={{ borderBottom: "1px solid rgba(255,255,255,0.04)", py: 1.2, display: "flex", gap: 2, alignItems: "flex-start" }}>
                                    {/* Image */}
                                    <MDBox sx={{ width: "60px", height: "45px", borderRadius: "2px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)", flexShrink: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", background: "#1a1a1a" }}
                                        onClick={() => { if (!fileRefs.current[i]) return; fileRefs.current[i].click(); }}>
                                        <input type="file" accept="image/*" ref={el => fileRefs.current[i] = el} style={{display:"none"}}
                                            onChange={e => { if(e.target.files[0]) handleUploadImage(w.name, e.target.files[0]); }} />
                                        {w.hasImage ? (
                                            <img src={window.location.origin + w.dbEntry.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                        ) : (
                                            <Icon sx={{ color: "#444", fontSize: "16px !important" }}>add_photo_alternate</Icon>
                                        )}
                                    </MDBox>

                                    {/* Info */}
                                    <MDBox sx={{ flex: 1, minWidth: 0 }}>
                                        <MDBox display="flex" alignItems="center" gap={1} mb={0.3}>
                                            <MDTypography sx={{ color: w.visible ? "#fff" : "#555", fontSize: "13px", fontWeight: 500 }}>{w.name}</MDTypography>
                                            <span style={{ fontSize: "9px", color: roleColor(w.role), background: w.role === "offensive" ? "rgba(220,50,50,0.1)" : "rgba(76,175,80,0.1)", border: "1px solid " + (w.role === "offensive" ? "rgba(220,50,50,0.2)" : "rgba(76,175,80,0.2)"), borderRadius: "2px", padding: "0 5px" }}>{w.role}</span>
                                        </MDBox>
                                        <MDBox display="flex" gap={1} flexWrap="wrap" mb={0.3}>
                                            {w.designation && <span style={{ fontSize: "10px", color: "#C29717" }}>{w.designation}</span>}
                                            <span style={{ fontSize: "10px", color: "#888" }}>{w.manufacturer}</span>
                                            <span style={{ fontSize: "10px", color: "#888" }}>{w.operator}</span>
                                            <span style={{ fontSize: "10px", color: "#666" }}>{w.type} · {w.range_km}km · {w.warhead}</span>
                                            <span style={{ fontSize: "10px", color: "#C29717" }}>x{w.frequency}</span>
                                        </MDBox>
                                        <MDBox display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
                                            {w.usedBy && w.usedBy.length > 0 && <>
                                                <span style={{fontSize:"9px",color:"#888",marginRight:"2px"}}>Used by:</span>
                                                {w.usedBy.map((co,ci) => <span key={ci} style={{fontSize:"9px",color:"#7a9ec2",background:"rgba(122,158,194,0.1)",border:"1px solid rgba(122,158,194,0.2)",borderRadius:"2px",padding:"0 4px"}}>{co}</span>)}
                                            </>}
                                            {w.usedAgainst && w.usedAgainst.length > 0 && <>
                                                <span style={{fontSize:"9px",color:"#888",marginLeft:"6px",marginRight:"2px"}}>Against:</span>
                                                {w.usedAgainst.map((co,ci) => <span key={ci} style={{fontSize:"9px",color:"#e88a2a",background:"rgba(232,138,42,0.1)",border:"1px solid rgba(232,138,42,0.2)",borderRadius:"2px",padding:"0 4px"}}>{co}</span>)}
                                            </>}
                                        </MDBox>
                                    </MDBox>

                                    <MDBox display="flex" gap={0.8} flexShrink={0} alignItems="center">
                                        <span onClick={() => handleToggleVisible(w.name, w.visible)} style={{width:'14px',height:'14px',borderRadius:'2px',border:'1px solid '+(w.visible?'#4CAF50':'rgba(255,255,255,0.15)'),background:w.visible?'rgba(76,175,80,0.15)':'transparent',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',fontSize:'9px',color:'#4CAF50'}}>{w.visible ? '✓' : ''}</span>
                                        {w.dbEntry && (
                                            <Icon sx={{ fontSize: "14px !important", color: "#888", cursor: "pointer", "&:hover": { color: "#e55" } }}
                                                onClick={() => handleDelete(w.dbEntry._id)}>close</Icon>
                                        )}
                                    </MDBox>
                                </MDBox>
                            ))
                        )}
                    </MDBox>
                </Card>
            </MDBox>
        </DashboardLayout>
    );
}

export default AdminArsenal;
